import {
  useState,
  useMemo,
  useContext,
  createContext,
  useCallback,
  useEffect,
} from 'react';
import {
  Autocomplete as MuiAutocomplete,
  Box,
  DialogContent,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FileMosaic } from '@files-ui/react';
import { set } from 'lodash-es';
import { FullScreenDialog } from '../../components/FullScreenDialog';
import { DialogActions } from '../../components/DialogActions';
import { Dropzone as RealDropZone } from '../../components/Dropzone';
import { getOrganization } from '../../lib/api/organizations.js';
import { Loading } from './Loading.jsx';
import ErrorMessage from '../../components/ErrorMessage';
import { TextField } from '../../components/inputs/TextField';
import { useFeatureFlag } from '../../hooks/use-feature-flag';

const requiredAttributes = ['name'];

const emptyOrganization = {
  name: '',
  isActive: true,
  contractTypes: [],
};

const FormContext = createContext();
const useFormState = () => useContext(FormContext);

export default function OrganizationDetails(props) {
  const [organization, setOrganization] = useState({
    ...emptyOrganization,
    ...props.organization,
    contractTypes: props.organization?.contractTypes || [],
  });
  const [logo, setLogo] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const isNew = useMemo(() => !organization.id, [organization.id]);

  useEffect(() => {
    async function fetchLogo() {
      if (!isNew) {
        setIsLoading(true);
        const { logoUrl } = await getOrganization(organization.id);

        setLogo(logoUrl);
        setIsLoading(false);
      }
    }
    fetchLogo();
  }, [isNew, organization.id]);

  const updateOrganization = useCallback(({ name, value }) => {
    setOrganization((current) => ({ ...set(current, name, value) }));
  }, []);

  const formState = {
    ...props,
    organization,
    updateOrganization,
    isNew,
    logo,
    setLogo,
    isLoading,
    error,
    setError,
  };

  return (
    <FormContext.Provider value={formState}>
      <FullScreenDialog>
        <DialogBody />
        <DialogFooter />
      </FullScreenDialog>
    </FormContext.Provider>
  );
}

function DialogBody() {
  const { isLoading } = useFormState();

  return (
    <DialogContent sx={{ px: { sx: 3, sm: 7.5 }, py: { xs: 3, sm: 4.5 } }}>
      {isLoading ? <Loading /> : <Form />}
    </DialogContent>
  );
}
function Form() {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <Error />
      <Fields />
    </Box>
  );
}

function Header() {
  const { isNew } = useFormState();

  return (
    <>
      <Typography variant="h4">{`${isNew ? 'New' : 'Edit'} Organization`}</Typography>
      <Typography variant="caption">
        {`${isNew ? 'Add' : 'Edit'} organization information using the text fields below`}
      </Typography>
    </>
  );
}

function Error() {
  const { error } = useFormState();
  if (!error) return null;
  return <ErrorMessage sx={{ mt: 2, mx: { md: 2 } }} error={error} />;
}

function Fields(props) {
  return (
    <Grid container sx={{ ...props.sx, mt: 2, px: { md: 2 } }}>
      <RequiredFields xs={12} md={6} sx={{ pr: { md: 5 } }} />
      <OptionalFields
        xs={12}
        md={6}
        sx={{
          mt: { xs: 3, md: 0 },
          pl: { md: 5 },
          borderLeft: { md: '1px solid rgba(0, 0, 0, 0.12)' },
        }}
      />
    </Grid>
  );
}

function RequiredFields(props) {
  const { organization, updateOrganization, error, contractTypes } =
    useFormState();

  return (
    <Grid item {...props}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Required Fields
      </Typography>

      <Grid container spacing={{ xs: 3, sm: 5 }}>
        <Grid container item direction="column" xs={12} sm={9} spacing={3}>
          <Grid item sx={{ width: '100%' }}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Name"
              variant="outlined"
              fullWidth
              value={organization.name}
              name="name"
              onChange={(e) => updateOrganization(e.target)}
              error={error?.validationErrors?.name?.message}
            />
          </Grid>
          <Grid
            item
            sx={{
              width: '100%',
              '& .MuiAutocomplete-tag': {
                maxWidth: 'calc(100% - 45px)',
              },
            }}
          >
            <MuiAutocomplete
              options={contractTypes}
              fullWidth
              disableCloseOnSelect
              disableClearable
              multiple
              name={'contractTypes'}
              value={organization.contractTypes}
              onChange={(e, value) =>
                updateOrganization({ name: 'contractTypes', value })
              }
              getOptionKey={(option) => option.id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(contractType) => contractType.name}
              renderOption={({ key, ...optionProps }, option, { selected }) => (
                <li key={key} {...optionProps}>
                  <Checkbox checked={selected} />
                  {option.name}
                </li>
              )}
              renderInput={(renderProps) => (
                <TextField {...renderProps} label="Contract" />
              )}
            />
          </Grid>
          <Grid item sx={{ width: '100%' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={organization.isActive}
                  onChange={(e) =>
                    updateOrganization({
                      name: 'isActive',
                      value: e.target.checked,
                    })
                  }
                />
              }
              label="Active"
            />
          </Grid>
          <Grid item sx={{ width: '100%' }}>
            <Dropzone />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

function OptionalFields(props) {
  const { organization, updateOrganization, enterprises } = useFormState();
  const isFarmqaSoilSamplingEnabled = useFeatureFlag('FarmqaSoilSampling');

  return (
    <Grid item {...props}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Optional Fields
      </Typography>

      <Grid container spacing={{ xs: 3, sm: 5 }}>
        <Grid container item direction="column" xs={12} sm={9} spacing={3}>
          <Grid item sx={{ width: '100%' }}>
            <TextField
              fullWidth
              select
              label="Enterprise"
              name="enterprise.id"
              tabIndex={25}
              value={organization.enterprise?.id || ''}
              onChange={(e) => updateOrganization(e.target)}
            >
              <MenuItem value={null}>{'None'}</MenuItem>
              {enterprises.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {isFarmqaSoilSamplingEnabled ? (
            <Grid item sx={{ width: '100%' }}>
              <TextField
                InputLabelProps={{ shrink: true }}
                label="SureTech Account Number"
                variant="outlined"
                fullWidth
                value={organization.suretechAccountNumber}
                name="suretechAccountNumber"
                onChange={(e) => updateOrganization(e.target)}
              />
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
}

function Dropzone() {
  const { updateOrganization, logo, setLogo } = useFormState();

  return (
    <RealDropZone
      onChange={(file) =>
        updateOrganization({
          name: 'logo',
          value: file[0],
        })
      }
      fileType="image"
      accept=".png,.jpeg,.svg"
      maxFiles={1}
      renderPreview={buildDropzoneContent(logo, setLogo, updateOrganization)}
    />
  );
}

function buildDropzoneContent(logo, setLogo, updateOrganization) {
  return (files) => {
    const isNewLogo = !logo && files.length;
    const FileMosaicProps = {
      ...(isNewLogo ? files[0] : { imageUrl: logo, type: 'image/png' }),
      preview: true,
      style: { width: 200, height: 200 },
      backgroundBlurImage: false,
      name: '',
      onDelete: () => removeLogo(isNewLogo, files),
    };

    return files.length || logo ? (
      <Box
        sx={{
          '& .files-ui-file-mosaic-file-name, & .file-mosaic-main-layer-footer':
            {
              display: 'none !important',
            },
        }}
      >
        <FileMosaic {...FileMosaicProps} />
      </Box>
    ) : (
      <Box>
        <Typography variant="h5">{'Upload Logo'}</Typography>
        <Typography>{'Click or drag and drop to upload image'}</Typography>
      </Box>
    );
  };

  function removeLogo(isNewLogo, files) {
    if (isNewLogo) {
      files.length = 0;
    }
    setLogo(null);

    updateOrganization({
      name: 'logo',
      value: null,
    });
  }
}

function DialogFooter() {
  const { organization, handleClose, handleSave, setError } = useFormState();
  const [isSaving, setSaving] = useState(false);

  const canSave = useMemo(
    () =>
      requiredAttributes.every((attribute) => {
        const value = organization[attribute];
        if (typeof value === 'string') return Boolean(value.trim());
        if (typeof value === 'object') return value.length > 0;
        return value;
      }),
    [organization],
  );

  async function save() {
    try {
      setError(null);
      setSaving(true);
      await handleSave(organization);
    } catch (e) {
      setSaving(false);
      setError(e);
    }
  }

  return (
    <DialogActions dark sx={{ py: 2, px: 3, mt: 3, gap: 2 }}>
      <Button
        disabled={isSaving}
        variant="contained"
        color="inherit"
        onClick={handleClose}
      >
        Cancel
      </Button>
      <LoadingButton
        disabled={!canSave}
        variant="contained"
        onClick={save}
        loading={isSaving}
      >
        Save
      </LoadingButton>
    </DialogActions>
  );
}
